/*
 * 收藏消息 —— 右上角爱心图标打开的页面。
 *
 * 旧版对应 `packages/components/src/characterUserProjection/FavorMessages.tsx`：
 * 在聊天里点赞过的**缈缈说过的话**，按时间倒序排一条时间线，
 * 每条显示日期 + 用户自己填的关键词 + 台词首行，点开看完整的一问一答。
 *
 * ⚠ 这个入口曾经被指到「羁绊」数值页（等级、好感、灵力流水、充值档位）。
 * 那是旧版**左上角**那块数值面板的内容，不是爱心图标。已按要求改回收藏消息。
 *
 * 关键词不只是标签：旧版会把命中关键词的收藏重新塞回提示词参与召回
 * （`characterRelationProjection.service.ts` 里按 `rate: 1` + tag 命中过滤），
 * 所以页面上要把这件事说清楚，否则用户不知道填它有什么用。
 * ⚠ **新系统的召回还没读这批 tag**（走的是 `MemoryIndex` 向量），
 * 所以文案上只说「她能想起来」，不承诺关键词一定生效。
 *
 * ## 取消收藏就在这一页，而且是唯一的整理入口
 *
 * 聊天里那颗心只能在当前会话里点到。想清理三个月前收藏的东西，
 * 只能在这里——没有这颗按钮的话，收藏是个只进不出的口袋。
 *
 * 取消走的是 `rateMessage(id, null)`，跟聊天里再点一次心是同一个接口：
 * 收藏没有独立的表，它就是 `MessageFeedback.rating === 1`。
 *
 * 取消之后**不重新拉列表**，本地把那条抹掉即可。重拉会让页面跳回第一页，
 * 用户正翻到第 4 页清理旧收藏时，每删一条就被弹回顶部。
 */

import { useCallback, useState } from "react";
import { LoadingState, PageNotice } from "../../components/AsyncState";
import { Button, PageShell, Sheet } from "../../components/ui";
import { IconHeartOutline, IconRetry } from "../../components/Icons";
import {
  fetchFavorites,
  type FavoriteList,
  type FavoriteMessage,
} from "../../services/me-api";
import { rateMessage } from "../../services/chat-api";
import { useAppearance } from "../../state/session";
import { useResource } from "../../state/resource";
import "../pages.css";

export function FavoritesPage() {
  const data = useResource<FavoriteList>((signal) => fetchFavorites(signal), []);
  const appearance = useAppearance();

  /*
   * 首屏那一页由 useResource 管，翻出来的后续页面累加在这里。
   * `removed` 单独记，不去改上面两份数据——取消收藏要同时对两者生效，
   * 而首屏那份的所有权在 useResource 手里。
   */
  const [morePages, setMorePages] = useState<FavoriteMessage[]>([]);
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [removing, setRemoving] = useState(false);

  const list = data.state.phase === "ready" ? data.state.data : null;
  const failed = data.state.phase === "failed";

  const loaded = [...(list?.favorites ?? []), ...morePages];
  const favorites = loaded.filter((f) => !removed.has(f.id));
  const opened = favorites.find((f) => f.id === openId) ?? null;

  /*
   * 还有没有下一页，要用**已加载条数**跟 total 比，不能用第一页的 hasMore：
   * 那个值描述的是「第一页之后还有没有」，翻到底之后它仍然是 true。
   * 已取消的那些不参与计数——它们在服务端已经不是收藏了，
   * 拿 favorites.length 当游标会让下一页跳过同样多的条目。
   */
  const total = list?.total ?? 0;
  const hasMore = loaded.length < total - removed.size;

  const loadMore = useCallback(() => {
    setLoadingMore(true);
    setMoreFailed(false);
    // 游标是「服务端那边已经翻过多少条」，所以用 loaded 而不是 favorites。
    fetchFavorites(undefined, loaded.length - removed.size)
      .then((page) => setMorePages((prev) => [...prev, ...page.favorites]))
      .catch(() => setMoreFailed(true))
      .finally(() => setLoadingMore(false));
  }, [loaded.length, removed.size]);

  const unfavorite = useCallback((id: string) => {
    setRemoving(true);
    rateMessage(id, null)
      .then(() => {
        setRemoved((prev) => new Set(prev).add(id));
        setOpenId(null);
      })
      // 失败不静默：不改本地状态，那条还留在列表里，与服务端一致。
      .catch(() => undefined)
      .finally(() => setRemoving(false));
  }, []);

  return (
    <PageShell
      icon={<IconHeartOutline size={20} />}
      title="收藏消息"
      help={
        <>
          <p>
            在聊天里点了小心心的话，都会悄悄跑到这里来，
            {appearance.name}把它们收在一个小盒子里啦。
          </p>
          <p>
            点开任意一条，能看到当时你说了什么、{appearance.name}
            又是怎么回的。不想留着的，在里面点「取消收藏」就好。
          </p>
        </>
      }
      actions={
        failed ? (
          <Button tone="quiet" icon={<IconRetry size={15} />} onClick={data.reload}>
            重试
          </Button>
        ) : undefined
      }
    >
      {data.state.phase === "loading" ? (
        <LoadingState label="正在取回收藏" />
      ) : null}

      {/* 这里必须写完整判断而不是用上面的 failed：布尔量不参与类型收窄，
          data.state.error 会被当成可能为 null。 */}
      {data.state.phase === "failed" ? (
        <PageNotice
          scene="favorites"
          error={data.state.error}
          onRetry={data.reload}
          expected={[
            "点赞过的角色台词，按时间倒序",
            "每条收藏的关键词，以及触发它的那句用户输入",
            "取消收藏的入口",
          ]}
        />
      ) : null}

      {list && favorites.length === 0 ? (
        <p className="inline-hint">
          还没有收藏。在对话里点一下喜欢的那句话，它就会留在这里。
        </p>
      ) : null}

      {favorites.length > 0 ? (
        <>
          <p className="inline-hint">
            共 {total - removed.size} 条，已显示 {favorites.length} 条
          </p>
          <div className="timeline">
            {favorites.map((fav) => (
              <button
                key={fav.id}
                type="button"
                className="timeline__item"
                onClick={() => setOpenId(fav.id)}
              >
                <span className="timeline__head">
                  <span className="timeline__date">
                    {fav.favoritedAt.slice(0, 10)}
                  </span>
                  {fav.tags.map((tag) => (
                    <span key={tag} className="chip chip--brand">
                      {tag}
                    </span>
                  ))}
                </span>
                <span className="timeline__text">{fav.text}</span>
              </button>
            ))}
          </div>
        </>
      ) : null}

      {hasMore ? (
        <Button block tone="quiet" disabled={loadingMore} onClick={loadMore}>
          {loadingMore ? "正在翻出更早的…" : "看看更早的收藏"}
        </Button>
      ) : null}

      {moreFailed ? (
        <p className="inline-hint">没能翻出更早的那些，稍后再试试。</p>
      ) : null}

      <Sheet
        open={opened !== null}
        title="收藏的话"
        onClose={() => setOpenId(null)}
      >
        {opened ? (
          <div className="favor-detail">
            {/* 旧版详情把用户那句也一起显示，不然只看回答不知道在聊什么。 */}
            {opened.userInput ? (
              <div className="favor-detail__turn">
                <p className="record-list__meta">你说</p>
                <p>{opened.userInput}</p>
              </div>
            ) : null}

            <div className="favor-detail__turn favor-detail__turn--character">
              <p className="record-list__meta">{appearance.name}说</p>
              <p>{opened.text}</p>
            </div>

            {opened.tags.length > 0 ? (
              <div className="favor-detail__tags">
                {opened.tags.map((tag) => (
                  <span key={tag} className="chip chip--brand">
                    {tag}
                  </span>
                ))}
              </div>
            ) : null}

            {/*
              取消收藏不做二次确认：它是可逆的（回到那条消息再点一次心就回来了），
              而且这一页的主要用途就是清理。每删一条弹一次确认会让整理变成折磨。
              删除消息那种不可逆操作才需要确认。
            */}
            <Button
              tone="quiet"
              block
              disabled={removing}
              onClick={() => unfavorite(opened.id)}
            >
              {removing ? "正在取消…" : "取消收藏"}
            </Button>
          </div>
        ) : null}
      </Sheet>
    </PageShell>
  );
}
